import { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, Copy, ExternalLink } from "lucide-react";
import { detectDuplicates, DuplicateMatch } from "@/lib/ai/duplicateDetector";
import { Voucher, VoucherType } from "@/lib/accounting/types";

interface Props {
  vouchers: Voucher[];
  type?: VoucherType;
  invoiceNumber?: string;
  partyLedgerId?: string;
  partyName?: string;
  totalAmount?: number;
  narration?: string;
  date?: string;
}

export function DuplicateEntryWarning({
  vouchers, type, invoiceNumber, partyLedgerId, partyName, totalAmount, narration, date,
}: Props) {
  const duplicates = useMemo(
    () =>
      detectDuplicates(vouchers, { type, invoiceNumber, partyLedgerId, partyName, totalAmount, narration, date }),
    [vouchers, type, invoiceNumber, partyLedgerId, partyName, totalAmount, narration, date]
  );

  if (duplicates.length === 0) return null;

  const highest = duplicates[0];
  const isCritical = highest.confidence >= 0.85;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        className={`rounded-lg border p-3 space-y-2 ${
          isCritical
            ? "bg-destructive/5 border-destructive/30"
            : "bg-warning/5 border-warning/30"
        }`}
      >
        <div className="flex items-start gap-2">
          <AlertTriangle
            className={`h-4 w-4 flex-shrink-0 mt-0.5 ${
              isCritical ? "text-destructive" : "text-warning"
            }`}
          />
          <div className="flex-1 min-w-0">
            <p
              className={`text-xs font-bold ${
                isCritical ? "text-destructive" : "text-warning"
              }`}
            >
              {isCritical ? "⚠️ Likely Duplicate Entry Detected" : "⚡ Possible Duplicate Entry"}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              AI detected {duplicates.length} potential match{duplicates.length > 1 ? "es" : ""} in
              existing entries.
            </p>
          </div>
          <span
            className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${
              isCritical
                ? "bg-destructive/10 text-destructive"
                : "bg-warning/10 text-warning"
            }`}
          >
            {(highest.confidence * 100).toFixed(0)}% match
          </span>
        </div>

        <div className="space-y-1.5">
          {duplicates.map((d, i) => (
            <div
              key={d.matchedVoucher.id}
              className="flex items-start gap-2 p-2 rounded-md bg-background/60 border border-border/50"
            >
              <Copy className="h-3 w-3 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] text-foreground leading-snug">{d.reason}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {d.matchType === "exact_invoice" && "Exact invoice number match"}
                  {d.matchType === "amount_party_date" && "Same amount, party & date"}
                  {d.matchType === "amount_date" && "Same amount & date"}
                  {d.matchType === "similar_narration" && "Similar description"}
                  {" · "}
                  {(d.confidence * 100).toFixed(0)}% confidence
                </p>
              </div>
            </div>
          ))}
        </div>

        <p className="text-[10px] text-muted-foreground italic">
          Review carefully before saving. You can still proceed if this is a legitimate entry.
        </p>
      </motion.div>
    </AnimatePresence>
  );
}
